/**
 * Document panel beside the ledger (Movements idea 3): the document's facts and lines, Print as a real
 * GRN / DN, Reverse… (an opposite movement with a reason — nothing deleted) and attachments (the signed
 * supplier DN photo / PDF). Idea 5: the GRN's supplier and purchase order ("Link PO"), the Site DN's
 * project and system.
 */
import { useEffect, useRef, useState } from 'react';
import { CheckCheck, CheckCircle2, Clock, History, ImageIcon, Link2, Loader2, Paperclip, Printer, RotateCcw, Search, Trash2, TriangleAlert, Truck, Upload, X } from 'lucide-react';
import api, { openAuthenticatedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill } from '@/components/ui/status-pill';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDateTime, formatNumber, formatTimeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { InvCard, LinkButton, PersonAvatar, UpperLabel } from './InventoryUi';
import {
  MOVEMENT_KIND_LABEL,
  MOVEMENT_KIND_TONE,
  documentLabel,
  formatQty,
  itemDataIssue,
  movementDirection,
  plural,
  signedQty,
  siteDnReceipt
} from './inventoryModel';

const signed = (value) => {
  const number = Number(value) || 0;
  if (number > 0) return `+${formatQty(number)}`;
  if (number < 0) return `−${formatQty(Math.abs(number))}`;
  return formatQty(number);
};

/** Facts of a document shared by the panel and the printed document. */
export function documentFacts(group, projectName = '') {
  const first = group.movements[0] || {};
  const project = projectName || first.project_name || '';
  const supplier = first.supplier_name || '';
  switch (group.kind) {
    case 'GRN': return { from: supplier ? `Supplier — ${supplier}` : 'Supplier', to: group.destination || '—', project };
    case 'site-dn': return { from: group.direct ? (supplier || 'Supplier (direct)') : (group.source || '—'), to: project || 'Site', project };
    case 'transfer': return { from: group.source || '—', to: group.destination || '—', project };
    case 'testing': return { from: group.source || '—', to: 'Lab use', project };
    case 'return': return { from: project || 'Site', to: group.destination || '—', project };
    default: return { from: group.source || group.destination || '—', to: group.destination || group.source || '—', project };
  }
}

export function attachmentsOf(group) {
  const byId = new Map();
  group.movements.forEach((movement) => (movement.attachments || []).forEach((attachment) => {
    if (attachment?.id && !byId.has(attachment.id)) byId.set(attachment.id, attachment);
  }));
  return [...byId.values()];
}

const fileSize = (bytes) => {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) return `${formatNumber(value / (1024 * 1024), { maxDecimals: 1 })} MB`;
  return `${formatNumber(Math.max(1, Math.round(value / 1024)))} KB`;
};

export default function MovementDocumentPanel({
  group,
  projectName = '',
  projectSystem = '',
  itemDescriptions = {},
  canReverse = false,
  canLinkPo = false,
  canAttach = true,
  canManageFiles = false,
  currentUserId = '',
  onClose,
  onPrint,
  onReversed,
  onChanged,
  onOpenHistory,
  onConfirmReceipt,
  canConfirmReceipt = false,
  onFixItem,
  fixLabelOf = () => 'Fix in Catalog'
}) {
  const [reverseOpen, setReverseOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  if (!group) return null;

  const first = group.movements[0] || {};
  const facts = documentFacts(group, projectName);
  const ids = group.movements.map((movement) => movement.id).filter(Boolean);
  const reversedLines = group.movements.filter((movement) => movement.reversed_by_movement_id);
  const fullyReversed = reversedLines.length > 0 && reversedLines.length === group.movements.length;
  const isReversal = group.movements.some((movement) => movement.reverses_movement_id);
  // A Procurement delivery note is corrected in Procurement: deleting it there also takes its stock back.
  const procurementDn = group.movements.find((movement) => movement.dn_from_procurement);
  const reversible = !fullyReversed && !isReversal && !group.direct && !procurementDn && ids.length > 0;
  const reverseHint = !canReverse
    ? 'Only Admin, Super Admin and Storekeeper can reverse documents'
    : procurementDn
      ? `Recorded through Procurement delivery note ${procurementDn.dn_number || group.reference} — correct or delete it in Procurement › Delivery notes`
      : !reversible ? 'This document cannot be reversed' : undefined;
  const attachments = attachmentsOf(group);
  const total = group.neutral ? group.qtyTotal : group.signedTotal;
  const printLabel = group.kind === 'GRN' ? 'Print GRN' : group.kind === 'site-dn' ? 'Print DN' : 'Print document';
  const lpoNumber = first.lpo_number || '';
  const receipt = siteDnReceipt(group);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('movement_ids', ids.join(','));
      form.append('file', file);
      await api.post('/stock/movement-documents/attachments', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      notify.success(`Attached ${file.name} to ${documentLabel(group)}`);
      onChanged?.();
    } catch (err) {
      notify.error('Could not attach the file', { description: getErrorMessage(err) });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAttachment = async (attachment) => {
    try {
      await api.delete(`/stock/movement-documents/attachments/${attachment.id}`);
      notify.success(`Removed ${attachment.file_name}`);
      onChanged?.();
    } catch (err) {
      notify.error('Could not remove the attachment', { description: getErrorMessage(err) });
    }
  };

  const open = async (attachment) => {
    try {
      await openAuthenticatedFile(attachment.file_url);
    } catch (err) {
      notify.error('Could not open the file', { description: getErrorMessage(err) });
    }
  };

  return (
    <InvCard className="scroll-mt-[calc(var(--app-header-height,0px)+12px)] xl:sticky xl:top-[calc(var(--app-header-height)+16px)]" data-testid="movement-document-panel" aria-label={`${documentLabel(group)} details`}>
      <div className="flex items-center gap-2 border-b border-line-2 px-5 py-3.5">
        <StatusPill tone={MOVEMENT_KIND_TONE[group.kind] || 'gray'} label={MOVEMENT_KIND_LABEL[group.kind]} />
        <h3 className={cn('min-w-0 flex-1 truncate font-heading text-[15px] font-bold text-ink', group.reference && group.kind !== 'adjustment' && 'font-mono')}>{documentLabel(group)}</h3>
        {onOpenHistory ? (
          <Button type="button" size="icon-sm" variant="ghost" className="text-ink-3" onClick={onOpenHistory} aria-label="Inventory history" title="History">
            <History aria-hidden="true" />
          </Button>
        ) : null}
        <Button type="button" size="icon-sm" variant="ghost" className="text-ink-3" onClick={onClose} aria-label="Close document" data-testid="movement-document-close">
          <X aria-hidden="true" />
        </Button>
      </div>

      <div className="space-y-4 px-5 py-4">
        {fullyReversed || reversedLines.length ? (
          <p className="flex items-start gap-2 rounded-lg bg-status-amber-bg px-3 py-2 text-[12.5px] text-status-amber-ink" data-testid="movement-document-reversed">
            <RotateCcw className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              <b className="font-bold">{fullyReversed ? 'Reversed' : `${reversedLines.length} of ${group.movements.length} lines reversed`}</b>
              {reversedLines[0]?.reversed_by ? ` by ${reversedLines[0].reversed_by}` : ''}
              {reversedLines[0]?.reversed_at ? ` · ${formatDate(reversedLines[0].reversed_at)}` : ''}
              {reversedLines[0]?.reversal_reason ? ` — ${reversedLines[0].reversal_reason}` : ''}
            </span>
          </p>
        ) : null}
        {isReversal ? (
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">
            {first.reference}{first.reason ? ` — ${first.reason}` : ''}
          </p>
        ) : null}

        {receipt ? (
          <ReceiptStatus receipt={receipt} canConfirm={canConfirmReceipt && Boolean(onConfirmReceipt)} onConfirm={() => onConfirmReceipt?.(group)} />
        ) : null}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]">
          <div>
            <dt className="text-[11.5px] text-ink-3">Date</dt>
            <dd className="mt-0.5 font-semibold text-ink">{formatDate(group.date)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11.5px] text-ink-3">{group.kind === 'GRN' ? 'Received by' : 'Recorded by'}</dt>
            <dd className="mt-0.5 flex min-w-0 items-center gap-1.5 font-semibold text-ink">
              {group.user ? <PersonAvatar name={group.user} size={20} /> : null}<span className="truncate">{group.user || '—'}</span>
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11.5px] text-ink-3">To</dt>
            <dd className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 font-semibold text-ink">
              <span className="truncate">{facts.to}</span>
              {group.kind === 'site-dn' && projectSystem ? <span className="rounded border border-line bg-surface-2 px-1 text-[10.5px] font-bold text-ink-2">{projectSystem}</span> : null}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11.5px] text-ink-3">From</dt>
            <dd className="mt-0.5 min-w-0 font-semibold text-ink">
              {group.kind === 'GRN' && !group.direct ? (
                <span className="flex flex-wrap items-center gap-x-1.5">
                  <span className="truncate">{first.supplier_name || 'Supplier'}</span>
                  <span className="text-ink-4" aria-hidden="true">—</span>
                  {canLinkPo ? (
                    <PoPicker movementIds={ids} current={lpoNumber} fromDeliveryNote={first.po_source === 'delivery_note'} onLinked={onChanged} />
                  ) : (
                    <span className="text-[12.5px] font-normal text-ink-3">{lpoNumber || 'No PO linked'}</span>
                  )}
                </span>
              ) : facts.from}
            </dd>
          </div>
          <div>
            <dt className="text-[11.5px] text-ink-3">Reference</dt>
            <dd className="mt-0.5 font-mono text-[12.5px] font-semibold text-ink">{group.reference || '—'}</dd>
          </div>
          <div>
            <dt className="text-[11.5px] text-ink-3">Total</dt>
            <dd className="mt-0.5 font-semibold text-ink">{plural(group.lines, 'line')} · {group.neutral ? formatQty(total) : signed(total)} pcs</dd>
          </div>
        </dl>

        <section aria-label="Lines">
          <UpperLabel className="mb-1">Lines</UpperLabel>
          <ul className="divide-y divide-line-2">
            {group.movements.map((movement, index) => {
              const description = movement.description || itemDescriptions[movement.part_number] || '';
              const issue = itemDataIssue({ ...movement, description });
              const neutral = movementDirection(movement) === 0;
              const qty = neutral ? formatQty(movement.qty) : signed(signedQty(movement));
              return (
                <li key={movement.id || index} className="flex min-h-[36px] items-center gap-2 py-1.5 text-[12.5px]">
                  <span className="shrink-0 font-mono text-[12px] text-ink">{movement.part_number || '—'}</span>
                  {issue && issue !== 'No description' ? (
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[11.5px] text-status-amber-ink">
                      <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" /><span className="truncate">{issue}</span>
                      {onFixItem ? (
                        <LinkButton className="shrink-0 text-[12px]" onClick={() => onFixItem(movement.part_number)} data-testid={`movement-panel-fix-${movement.id || index}`}>{fixLabelOf(movement.part_number)}</LinkButton>
                      ) : null}
                    </span>
                  ) : (
                    <span className={cn('min-w-0 flex-1 truncate', description ? 'text-ink-3' : 'text-status-amber-ink')}>{description || 'No description'}</span>
                  )}
                  <b className={cn('shrink-0 font-bold tabular-nums', !neutral && signedQty(movement) > 0 ? 'text-status-green-ink' : 'text-ink')}>{qty}</b>
                </li>
              );
            })}
          </ul>
        </section>

        <section aria-label="Attachments">
          <UpperLabel className="mb-1.5">Attachments</UpperLabel>
          {attachments.length ? (
            <ul className="mb-2 space-y-1.5">
              {attachments.map((attachment) => (
                <li key={attachment.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5 text-[12.5px]">
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                  <button type="button" onClick={() => open(attachment)} className="min-w-0 flex-1 truncate text-left font-semibold text-brand-ink hover:underline" title={attachment.file_name}>
                    {attachment.file_name}
                  </button>
                  <span className="shrink-0 text-[11px] text-ink-3">{fileSize(attachment.size)} · {attachment.uploaded_by}</span>
                  {canManageFiles || (attachment.uploaded_by_id && attachment.uploaded_by_id === currentUserId) ? (
                    <Button type="button" size="icon-xs" variant="ghost" className="text-ink-3" onClick={() => removeAttachment(attachment)} aria-label={`Remove ${attachment.file_name}`}>
                      <Trash2 aria-hidden="true" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {canAttach ? (
          <>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,application/pdf,image/*"
            className="sr-only"
            onChange={(event) => upload(event.target.files?.[0])}
            data-testid="movement-attachment-input"
            tabIndex={-1}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || attachments.length >= 10}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-[color:var(--line-dashed)] text-[13px] text-ink-3 hover:border-[color:var(--brand-ink)] hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            data-testid="movement-attach-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />}
            {uploading ? 'Uploading…' : `Attach ${group.kind === 'GRN' ? 'supplier DN' : 'signed DN'} photo or PDF`}
          </button>
          </>
          ) : !attachments.length ? <p className="text-[12.5px] text-ink-3">No attachments</p> : null}
        </section>
      </div>

      <div className="space-y-2 border-t border-line-2 px-5 py-4">
        <Button type="button" variant="outline" className="w-full text-ink" onClick={() => onPrint?.(group)} data-testid="movement-print-btn">
          <Printer aria-hidden="true" /> {printLabel}
        </Button>
        <span className="block" title={reverseHint}>
          <Button
            type="button"
            variant="outline"
            className="w-full text-ink"
            disabled={!canReverse || !reversible}
            onClick={() => setReverseOpen(true)}
            data-testid="movement-reverse-btn"
          >
            <RotateCcw aria-hidden="true" /> Reverse…
          </Button>
        </span>
        <p className="text-center text-[11.5px] text-ink-3" data-testid="movement-reverse-note">
          {procurementDn
            ? <>From Procurement delivery note <b className="font-semibold text-ink-2">{procurementDn.dn_number || group.reference}</b> — correct or delete it in Procurement.</>
            : 'Reverse adds an opposite movement with a reason — nothing is deleted.'}
        </p>
      </div>

      <ReverseDialog
        open={reverseOpen}
        onOpenChange={setReverseOpen}
        group={group}
        itemDescriptions={itemDescriptions}
        onReversed={onReversed}
      />
    </InvCard>
  );
}

function ReverseDialog({ open, onOpenChange, group, itemDescriptions, onReversed }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setReason(''); setError(''); } }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (!reason.trim()) { setError('Say why — it is saved with the reversal'); return; }
    setBusy(true);
    setError('');
    try {
      // Lines reversed earlier stay as they are; only the rest of the document is reversed.
      const open = group.movements.filter((movement) => !movement.reversed_by_movement_id).map((movement) => movement.id);
      await api.post('/stock/movement-documents/reverse', { movement_ids: open, reason: reason.trim() });
      notify.success(`${documentLabel(group)} reversed`);
      onOpenChange(false);
      onReversed?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not reverse the document'));
    } finally {
      setBusy(false);
    }
  };

  const effect = {
    GRN: `takes ${formatQty(group.qtyTotal)} pcs back out of ${group.destination || 'the store'}`,
    'site-dn': `puts ${formatQty(group.qtyTotal)} pcs back into ${group.source || 'the store'} and marks them not delivered`,
    transfer: `moves ${formatQty(group.qtyTotal)} pcs back from ${group.destination} to ${group.source}`,
    testing: `puts ${formatQty(group.qtyTotal)} pcs back into ${group.source || 'the store'}`,
    return: `takes ${formatQty(group.qtyTotal)} pcs back out of ${group.destination || 'the store'}`,
    adjustment: 'undoes the adjustment'
  }[group.kind] || 'records the opposite movement';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-ink">Reverse {documentLabel(group)}?</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            Records an opposite movement for {plural(group.lines, 'line')} — it {effect}. The original stays in the ledger, marked as reversed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <ul className="max-h-40 overflow-y-auto rounded-lg border border-line px-3 py-1.5 text-[12.5px]">
            {group.movements.map((movement) => (
              <li key={movement.id} className="flex items-center gap-2 py-1">
                <span className="font-mono">{movement.part_number || '—'}</span>
                <span className="min-w-0 flex-1 truncate text-ink-3">{movement.description || itemDescriptions[movement.part_number] || ''}</span>
                <b className="tabular-nums">{formatQty(Math.abs(Number(movement.qty) || 0))}</b>
              </li>
            ))}
          </ul>
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-ink-2">Reason · <span className="text-status-red-ink">required</span></span>
            <Textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={300} autoFocus placeholder="e.g. Supplier sent the wrong DN — re-entered as GRN 879" data-testid="movement-reverse-reason" />
          </label>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="movement-reverse-confirm">{busy ? 'Reversing…' : `Reverse ${documentLabel(group)}`}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Link PO" — search the purchase orders and link one to this GRN (or unlink it). */
function PoPicker({ movementIds, current, fromDeliveryNote, onLinked }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/stock/po-options', { params: query.trim() ? { search: query.trim() } : {} });
        if (!ignore) setOptions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!ignore) setError(getErrorMessage(err, 'Could not load purchase orders'));
      } finally {
        if (!ignore) setLoading(false);
      }
    }, 200);
    return () => { ignore = true; clearTimeout(timer); };
  }, [open, query]);

  const link = async (lpoId) => {
    try {
      const res = await api.put('/stock/movement-documents/po', { movement_ids: movementIds, lpo_id: lpoId });
      notify.success(lpoId ? `Linked to ${res.data?.lpo_number || 'the PO'}` : 'PO unlinked');
      setOpen(false);
      onLinked?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not link the PO'));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="movement-link-po">
          <Link2 className="h-3 w-3" aria-hidden="true" />{current ? current : 'Link PO'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={16} className="w-80 rounded-xl border-line bg-surface p-2 text-ink shadow-overlay">
        <label className="flex h-8 items-center gap-2 rounded-lg border border-line px-2.5 text-ink-4 focus-within:border-[color:var(--brand-ink)]">
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} autoFocus placeholder="PO number or supplier…" className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none" aria-label="Search purchase orders" />
        </label>
        {fromDeliveryNote ? <p className="mt-1.5 px-1 text-[11.5px] text-ink-3">Linked through its procurement delivery note. Picking one here overrides it for this GRN.</p> : null}
        <div className="mt-1.5 max-h-60 overflow-y-auto" role="listbox" aria-label="Purchase orders">
          {loading ? <p className="px-2 py-2 text-[12.5px] text-ink-3">Loading…</p> : null}
          {error ? <p className="px-2 py-2 text-[12.5px] text-status-red-ink" role="alert">{error}</p> : null}
          {!loading && !error && !options.length ? <p className="px-2 py-2 text-[12.5px] text-ink-3">No purchase orders found</p> : null}
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={option.lpo_number === current}
              onClick={() => link(option.id)}
              className="flex w-full flex-col rounded-lg px-2 py-1.5 text-left hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
            >
              <span className="font-mono text-[12.5px] font-semibold text-ink">{option.lpo_number || '—'}</span>
              <span className="truncate text-[11.5px] text-ink-3">{[option.supplier, option.project_name].filter(Boolean).join(' · ') || '—'}</span>
            </button>
          ))}
        </div>
        {current && !fromDeliveryNote ? (
          <Button type="button" size="sm" variant="ghost" className="mt-1 w-full justify-start text-ink-2" onClick={() => link('')}>Unlink {current}</Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Where a Site DN stands (17 #8 / 21 #9): "Received by Abd Alrahman · 2 Oct 09:15" with the signature and the
 * photo of the signed DN, or "Dispatched 2 days ago — not confirmed" with Confirm receipt.
 */
function ReceiptStatus({ receipt, canConfirm, onConfirm }) {
  if (receipt.state === 'received') {
    return (
      <section className="rounded-xl border border-[color:var(--status-green-bg)] bg-status-green-bg px-3 py-2.5" aria-label="Receipt on site" data-testid="movement-receipt-received">
        <p className="flex items-start gap-2 text-[13px] text-status-green-ink">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span><b className="font-bold">Received by {receipt.receivedBy || 'the site'}</b>{receipt.receivedAt ? ` · ${formatDateTime(receipt.receivedAt)}` : ''}</span>
        </p>
        {receipt.notes ? <p className="mt-1 pl-6 text-[12.5px] text-ink-2">“{receipt.notes}”</p> : null}
        {receipt.signatureUrl || receipt.photoUrl ? (
          <div className="mt-2 flex flex-wrap items-end gap-2 pl-6">
            {receipt.signatureUrl ? <ReceiptImage url={receipt.signatureUrl} alt={`Signature of ${receipt.receivedBy || 'the receiver'}`} testId="movement-receipt-signature" /> : null}
            {receipt.photoUrl ? (
              <button type="button" onClick={() => openAuthenticatedFile(receipt.photoUrl).catch((err) => notify.error('Could not open the photo', { description: getErrorMessage(err) }))} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline" data-testid="movement-receipt-photo">
                <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" /> Photo of the signed DN
              </button>
            ) : null}
          </div>
        ) : null}
        {receipt.confirmedBy && receipt.confirmedBy !== receipt.receivedBy ? <p className="mt-1 pl-6 text-[11.5px] text-ink-3">Confirmed in the app by {receipt.confirmedBy}</p> : null}
      </section>
    );
  }
  const late = receipt.state === 'overdue';
  return (
    <section className={cn('rounded-xl px-3 py-2.5', late ? 'bg-status-amber-bg' : 'bg-surface-2')} aria-label="Receipt on site" data-testid={`movement-receipt-${receipt.state}`}>
      <p className={cn('flex items-start gap-2 text-[13px]', late ? 'text-status-amber-ink' : 'text-ink-2')}>
        {late ? <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> : <Truck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
        <span>
          <b className="font-bold">{late ? 'Not confirmed on site' : 'On the way'}</b> · dispatched {formatTimeAgo(receipt.dispatchedAt)}
          <span className="block text-[12px] font-normal">{late ? 'Not signed for within 48 hours of leaving the store.' : 'Waiting for the site to sign for it.'}</span>
        </span>
      </p>
      {canConfirm ? (
        <Button type="button" size="sm" className="mt-2 w-full" onClick={onConfirm} data-testid="movement-confirm-receipt">
          <CheckCheck aria-hidden="true" /> Confirm receipt
        </Button>
      ) : null}
    </section>
  );
}

/** A receipt image fetched with the session token (the file needs it) — shown small, opens full size. */
function ReceiptImage({ url, alt, testId }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let objectUrl = '';
    let alive = true;
    const path = String(url || '').replace(/^\/api/, '');
    api.get(path, { responseType: 'blob' })
      .then((res) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (failed) return <span className="text-[12px] text-ink-3">Signature saved (could not load the image)</span>;
  return (
    <button type="button" onClick={() => openAuthenticatedFile(url).catch(() => {})} className="rounded-lg border border-line bg-white p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title="Open the signature" data-testid={testId}>
      {src ? <img src={src} alt={alt} className="h-12 w-auto max-w-[180px] object-contain" /> : <span className="block h-12 w-[120px] animate-pulse rounded bg-surface-2" aria-label="Loading signature" />}
    </button>
  );
}
